<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

// Switches for how the genie works on this make: looking things up first, and whether
// the user's saved details (name, company...) go into it
const store = useStore();

const research = computed(() => store.state.context.research);
const useProfile = computed(() => store.state.context.useProfile);
const loggedIn = computed(() => store.state.auth.loggedIn);
const researchCost = computed(() => store.state.wishes.costs?.research ?? 1);

const toggleResearch = () =>
  store.commit("context/SET_RESEARCH", !research.value);
const toggleProfile = () =>
  store.commit("context/SET_USE_PROFILE", !useProfile.value);
const editDetails = () => store.dispatch("drawer/setInfo");

const DETAILS = [
  ["name", "Name"],
  ["job", "Job"],
  ["company", "Company"],
  ["address", "Address"],
  ["phone", "Phone"],
  ["website", "Website"],
  ["signOff", "Sign-off"],
];
const savedDetails = computed(() =>
  DETAILS.filter(([field]) => String(store.state.personalInfo[field] || ""))
);

const options = computed(() => [
  {
    id: "research",
    icon: "globe",
    title: "Research the web first",
    text: `Looks up current facts, prices and news before writing, and lists its sources. Uses ${
      researchCost.value
    } extra wish${researchCost.value === 1 ? "" : "es"}.`,
    on: research.value,
    toggle: toggleResearch,
  },
  ...(loggedIn.value
    ? [
        {
          id: "profile",
          icon: "user",
          title: "Use my details",
          text: "Fills in your name, company and contact details where they fit. Turn off for makes about someone else.",
          on: useProfile.value,
          toggle: toggleProfile,
        },
      ]
    : []),
]);
</script>

<template>
  <section
    class="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white"
  >
    <div v-for="option in options" :key="option.id" class="p-4 sm:p-5">
      <button
        type="button"
        role="switch"
        :aria-checked="option.on"
        class="flex w-full items-center justify-between gap-4 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accentPrimary"
        @click="option.toggle"
      >
        <span class="flex items-start">
          <span
            class="mr-3 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors"
            :class="
              option.on
                ? 'bg-accentPrimary text-white'
                : 'bg-gray-10 text-secondary'
            "
          >
            <font-awesome-icon :icon="['fas', option.icon]" />
          </span>
          <span>
            <span class="block font-bold text-primary">{{ option.title }}</span>
            <span class="block text-sm text-secondary">{{ option.text }}</span>
          </span>
        </span>
        <span
          aria-hidden="true"
          class="relative h-6 w-11 shrink-0 rounded-full transition-colors"
          :class="option.on ? 'bg-accentPrimary' : 'bg-gray-200'"
        >
          <span
            class="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
            :class="option.on ? 'left-[1.4rem]' : 'left-0.5'"
          />
        </span>
      </button>
      <div
        v-if="option.id === 'profile' && useProfile"
        class="ml-11 mt-3 flex flex-wrap items-center gap-2"
      >
        <span
          v-for="[field, label] in savedDetails"
          :key="field"
          class="rounded-full bg-gray-10 px-2.5 py-0.5 text-xs text-primary"
          >{{ label }}</span
        >
        <span v-if="!savedDetails.length" class="text-xs text-secondary"
          >No details saved yet.</span
        >
        <button
          type="button"
          class="text-xs font-bold text-accentPrimary hover:text-purple-400"
          @click="editDetails"
        >
          {{ savedDetails.length ? "Edit details" : "Add your details" }}
        </button>
      </div>
    </div>
  </section>
</template>
